import React, { useState, useRef, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getCityTaxReport, CityTaxReport, CityTaxBasis, DEFAULT_CITY_TAX_BASIS } from '../api.cityTax';
import { generateCityTaxPdf } from '../utils/cityTaxPdf';
import { useLang } from '../i18n/LanguageContext';
import { cityTaxReportText } from '../i18n/pages/cityTaxReport';
import useIsMobile from '../hooks/useIsMobile';

const getToken = () => localStorage.getItem('token') || '';

const fmt = (n: number | undefined) =>
  n != null ? n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

const prevYearMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthToDateRange = (ym: string): { dateFrom: string; dateTo: string } => {
  const [y, m] = ym.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return {
    dateFrom: `${y}-${String(m).padStart(2, '0')}-01`,
    dateTo:   `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
};

const ymToDisplay = (ym: string) => {
  const [y, m] = ym.split('-');
  return `${m}/${y}`;
};

const fullStayNights = (checkIn: string, checkOut: string) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / msPerDay);
};

const BASIS_PARAM = 'basis';

const CityTaxReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const apartmentId = Number(id);
  const { lang } = useLang();
  const t = cityTaxReportText[lang];
  const isMobile = useIsMobile();

  // The basis lives in the URL (not in storage): a reload keeps it, a fresh visit starts at the default.
  const [searchParams, setSearchParams] = useSearchParams();
  const basis: CityTaxBasis = searchParams.get(BASIS_PARAM) === 'UBYPORT_FILED' ? 'UBYPORT_FILED' : DEFAULT_CITY_TAX_BASIS;
  const latestRequest = useRef(0);

  const [yearMonth, setYearMonth]   = useState(prevYearMonth);
  const [report, setReport]         = useState<CityTaxReport | null>(null);
  const [loading, setLoading]       = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const pdfInFlight = useRef(false);

  const calculate = async (forBasis: CityTaxBasis = basis) => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError(null);
    const { dateFrom, dateTo } = monthToDateRange(yearMonth);
    try {
      const r = await getCityTaxReport(apartmentId, dateFrom, dateTo, getToken(), forBasis);
      // A slower answer for the previous toggle position must not overwrite the newer one.
      if (request === latestRequest.current) setReport(r);
    } catch (err: any) {
      if (request === latestRequest.current) {
        setError(err.response?.data?.message || err.message || 'Failed to load report');
      }
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  };

  const changeBasis = (next: CityTaxBasis) => {
    if (next === basis) return;
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_CITY_TAX_BASIS) params.delete(BASIS_PARAM);
    else params.set(BASIS_PARAM, next);
    setSearchParams(params, { replace: true });
    // Only refetch when a report is already on screen; otherwise the choice is used by the next Calculate.
    if (report) void calculate(next);
  };

  const handleDownloadPdf = useCallback(async () => {
    if (!report || pdfInFlight.current) return;
    pdfInFlight.current = true;
    setPdfLoading(true);
    try {
      await generateCityTaxPdf(report, ymToDisplay(yearMonth));
      // 3-second cooldown after success to prevent accidental re-downloads
      setTimeout(() => { pdfInFlight.current = false; }, 3000);
    } catch (err: any) {
      setError(err.message || 'PDF generation failed');
      pdfInFlight.current = false;
    } finally {
      setPdfLoading(false);
    }
  }, [report, yearMonth]);

  const sortedReservations = report
    ? [...report.reservations].sort((a, b) => a.checkIn.localeCompare(b.checkIn))
    : [];

  return (
    <div style={{ maxWidth: 1000, margin: '30px auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 24 }}>
      <Link
        to={`/apartments/${apartmentId}?tab=citytax`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontWeight: 600, textDecoration: 'none', marginBottom: 20, fontSize: '0.9rem' }}
      >
        {t.backToApartment}
      </Link>
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>{t.pageTitle}</h2>
      <p style={{ color: '#718096', marginTop: 0, marginBottom: 20 }}>{t.subtitle}</p>

      {/* Controls toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
        background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '12px 16px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.85rem', color: '#4a5568', fontWeight: 500, whiteSpace: 'nowrap' }}>{t.labelMonth}:</span>
          <input
            type="month"
            className="form-control"
            value={yearMonth}
            onChange={e => setYearMonth(e.target.value)}
            style={{ width: 150 }}
          />
        </div>
        <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />
        <div role="group" aria-label={t.basisLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', color: '#4a5568', fontWeight: 500, whiteSpace: 'nowrap' }}>{t.basisLabel}:</span>
          <div style={{ display: 'inline-flex', border: '1px solid #cbd5e0', borderRadius: 8, overflow: 'hidden' }}>
            {([
              ['COMPLETED_GUESTS', t.basisCompleted],
              ['UBYPORT_FILED', t.basisFiled],
            ] as [CityTaxBasis, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={basis === value}
                onClick={() => changeBasis(value)}
                style={{
                  border: 'none', padding: '6px 12px', fontSize: '0.82rem', cursor: 'pointer',
                  fontWeight: basis === value ? 700 : 500,
                  background: basis === value ? '#2563eb' : '#fff',
                  color: basis === value ? '#fff' : '#4a5568',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />
        <button
          className="app-btn app-btn-green app-btn-standard"
          onClick={() => calculate()}
          disabled={loading}
          style={{ minWidth: 110 }}
        >
          {loading ? t.calculating : t.calculate}
        </button>
        {report && (
          <>
            <button
              className="app-btn app-btn-blue app-btn-standard"
              onClick={handleDownloadPdf}
              disabled={pdfLoading || !report.payerName || !report.mailboxName}
              style={{ minWidth: 140, display: 'flex', alignItems: 'center', gap: 6 }}
              title={!report.payerName ? t.pdfNoContact : !report.mailboxName ? t.pdfNoMailbox : undefined}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <polyline points="9 15 12 18 15 15"/>
              </svg>
              {pdfLoading ? t.pdfLoading : t.downloadPdf}
            </button>
            {(!report.payerName || !report.mailboxName) && (
              <span style={{ fontSize: '0.82rem', color: '#b7791f', fontStyle: 'italic' }}>
                {!report.payerName ? t.pdfNoContact : t.pdfNoMailbox}
              </span>
            )}
          </>
        )}
      </div>

      {error && <div style={{ color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 10, marginBottom: 16 }}>{error}</div>}

      {report && report.basis === 'UBYPORT_FILED' && (
        <div
          role="note"
          data-testid="city-tax-excluded-box"
          style={{
            background: (report.excludedGuests ?? 0) > 0 ? '#ebf8ff' : '#f7fafc',
            border: `1px solid ${(report.excludedGuests ?? 0) > 0 ? '#90cdf4' : '#e2e8f0'}`,
            color: '#2a4365', padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: '0.88rem',
          }}
        >
          {(report.excludedGuests ?? 0) > 0 ? t.excludedBox(report.excludedGuests ?? 0) : t.excludedNone}
        </div>
      )}

      {report && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: t.cardTotalTax, value: fmt(report.totalTax) + ' Kč', highlight: true },
              { label: t.cardReservations, value: String(report.totalReservations) },
              { label: t.cardAdults(fmt(report.adultRate)), value: t.guestsNights(report.adults.guests, report.adults.nights) },
              { label: t.cardChildren(fmt(report.childRate)), value: t.guestsNights(report.children.guests, report.children.nights) },
              { label: t.cardDisabled(fmt(report.disabledRate)), value: t.guestsNights(report.disabled.guests, report.disabled.nights) },
            ].map(card => (
              <div key={card.label} style={{ padding: '12px 16px', borderRadius: 10, background: card.highlight ? '#f0fdf4' : '#f7fafc', border: `1px solid ${card.highlight ? '#bbf7d0' : '#e2e8f0'}` }}>
                <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: 4 }}>{card.label}</div>
                <div style={{ fontWeight: 700, fontSize: card.highlight ? '1.25rem' : '1rem', color: card.highlight ? '#166534' : '#2d3748' }}>{card.value}</div>
                {card.highlight && (
                  <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 2 }}>
                    {t.breakdownLine(fmt(report.adults.tax), fmt(report.children.tax), fmt(report.disabled.tax))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Per-reservation breakdown — sorted by check-in date */}
          {sortedReservations.length === 0 ? (
            <div style={{ color: '#718096', fontStyle: 'italic' }}>{t.noReservations}</div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sortedReservations.map(row => {
                const totalNights = fullStayNights(row.checkIn, row.checkOut);
                const isPartial = totalNights > row.nights;
                return (
                  <div key={row.reservationId} style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <Link to={`/reservations/${row.reservationId}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
                          {row.guestName}
                        </Link>
                        <div style={{ color: '#718096', fontSize: '0.78rem' }}>#{row.reservationId}</div>
                        {!!row.excludedCount && (
                          <div title={t.excludedTooltip} style={{ color: '#b7791f', fontSize: '0.78rem', fontWeight: 600, cursor: 'help' }}>
                            {t.excludedLine(row.excludedCount)}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: '#166534' }}>{fmt(row.tax)} Kč</div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: 6 }}>
                      {row.checkIn} → {row.checkOut}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: '0.82rem', color: '#4a5568' }}>
                      <span>{t.thNights}: <strong>{row.nights}</strong></span>
                      {isPartial && (
                        <span
                          title={t.partialTooltip(row.nights, totalNights)}
                          style={{
                            fontSize: '0.7rem', fontWeight: 700, color: '#b7791f',
                            background: '#fefcbf', border: '1px solid #f6e05e', borderRadius: 6,
                            padding: '1px 6px', whiteSpace: 'nowrap', cursor: 'help',
                          }}
                        >
                          {t.partialBadge}
                        </span>
                      )}
                      <span>· {t.thAdults}: {row.adultCount || '—'}</span>
                      <span>· {t.thChildren}: {row.childCount || '—'}</span>
                      <span>· {t.thDisabled}: {row.disabledCount || '—'}</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>{t.total}</span>
                <span style={{ color: '#166534' }}>{fmt(report.totalTax)} Kč</span>
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={th}>{t.thReservationId}</th>
                    <th style={th}>{t.thGuest}</th>
                    <th style={th}>{t.thCheckIn}</th>
                    <th style={th}>{t.thCheckOut}</th>
                    <th style={{ ...th, textAlign: 'center' }}>{t.thNights}</th>
                    <th style={{ ...th, textAlign: 'center' }}>{t.thAdults}</th>
                    <th style={{ ...th, textAlign: 'center' }}>{t.thChildren}</th>
                    <th style={{ ...th, textAlign: 'center' }}>{t.thDisabled}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t.thTax}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReservations.map(row => {
                    const totalNights = fullStayNights(row.checkIn, row.checkOut);
                    const isPartial = totalNights > row.nights;
                    return (
                    <tr key={row.reservationId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ ...td, color: '#718096', fontSize: '0.82rem' }}>#{row.reservationId}</td>
                      <td style={td}>
                        <Link to={`/reservations/${row.reservationId}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                          {row.guestName}
                        </Link>
                        {!!row.excludedCount && (
                          <span
                            title={t.excludedTooltip}
                            style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 600, color: '#b7791f', cursor: 'help', whiteSpace: 'nowrap' }}
                          >
                            {t.excludedLine(row.excludedCount)}
                          </span>
                        )}
                      </td>
                      <td style={td}>{row.checkIn}</td>
                      <td style={td}>{row.checkOut}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {row.nights}
                        {isPartial && (
                          <span
                            title={t.partialTooltip(row.nights, totalNights)}
                            style={{
                              marginLeft: 6, fontSize: '0.7rem', fontWeight: 700, color: '#b7791f',
                              background: '#fefcbf', border: '1px solid #f6e05e', borderRadius: 6,
                              padding: '1px 6px', whiteSpace: 'nowrap', cursor: 'help',
                            }}
                          >
                            {t.partialBadge}
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>{row.adultCount || '—'}</td>
                      <td style={{ ...td, textAlign: 'center' }}>{row.childCount || '—'}</td>
                      <td style={{ ...td, textAlign: 'center' }}>{row.disabledCount || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(row.tax)}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f0fdf4' }}>
                    <td colSpan={8} style={{ ...td, fontWeight: 700 }}>{t.total}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#166534' }}>{fmt(report.totalTax)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const th: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#4a5568' };
const td: React.CSSProperties = { padding: '8px 12px', color: '#2d3748' };

export default CityTaxReportPage;
